/**
 * Project update sends `languages` as `[{ lang, model }, ...]` (no separate string list).
 * Legacy reads: `languages: string[]` + optional `language_models` map/array.
 */
export const DEFAULT_VOICE_AI_MODEL = 'gemini-2.5-pro';

export const VOICE_AI_MODEL_OPTIONS = [
  {
    id: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    /** Shown in UI (e.g. “Model 1”); API still receives `id`. */
    uiLabel: 'Model 1',
    /** Compact text for inline / pill UIs; falls back to `label` if omitted. */
    pillLabel: 'Gemini 2.5',
  },
  /** OpenAI speech/transcription id expected by data collector (not plain `gpt-4o`). */
  {
    id: 'gpt-4o-transcribe',
    label: 'GPT-4o transcribe',
    uiLabel: 'Model 2',
    pillLabel: 'GPT-4o',
  },
];

export const VOICE_PREVIEW_MODEL_OPTIONS = [
  {
    id: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    uiLabel: 'Model 1',
    pillLabel: 'Gemini 2.5',
  },
];

const VOICE_MODEL_API_ALIASES = {
  'gpt-4o': 'gpt-4o-transcribe',
};

/** Map stored or UI aliases to ids the API accepts. */
export function normalizeVoiceModelId(id) {
  if (id == null || id === '') return DEFAULT_VOICE_AI_MODEL;
  const trimmed = String(id).trim();
  return VOICE_MODEL_API_ALIASES[trimmed] || trimmed;
}

/** True when a real model was chosen / stored (not legacy “language only”). */
export function hasStoredVoiceModel(modelId) {
  return modelId != null && String(modelId).trim() !== '';
}

/** UI-only name; returns '' when no model (do not substitute default). */
export function getVoiceModelUiLabel(modelId) {
  if (!hasStoredVoiceModel(modelId)) return '';
  const id = normalizeVoiceModelId(modelId);
  const m = VOICE_AI_MODEL_OPTIONS.find((o) => o.id === id);
  return m?.uiLabel || m?.label || id;
}

/**
 * @param {unknown} languageModels — `{ lang, model }[]` or legacy `Record<lang, modelId>`
 * @param {string} langValue — e.g. `english`
 */
export function getLanguageModelForLang(languageModels, langValue) {
  if (languageModels == null || langValue == null) return undefined;
  if (Array.isArray(languageModels)) {
    const row = languageModels.find(
      (x) => x && (x.lang === langValue || x.language === langValue)
    );
    return row?.model != null && row.model !== '' ? String(row.model) : undefined;
  }
  if (typeof languageModels === 'object') {
    const v = languageModels[langValue];
    return v != null && v !== '' ? String(v) : undefined;
  }
  return undefined;
}

/**
 * Normalize GET project details `languages` into UI state `{ value, label, model }[]`.
 * Supports: `[{ lang, model }]`, legacy `string[]` + `language_models`, JSON string arrays.
 */
export function languagesStateFromProjectDetails(projectRow) {
  if (!projectRow) return [];
  let raw = projectRow.languages;
  if (raw == null) return [];
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];

  const legacyLangStrings = raw.length > 0 && typeof raw[0] === 'string';

  let rows = raw
    .map((language) => {
      if (typeof language === 'string') {
        const lower = language.toLowerCase();
        return {
          value: lower,
          label: lower.charAt(0).toUpperCase() + lower.slice(1),
          model: null,
        };
      }
      if (language && typeof language === 'object') {
        const lower = (
          language.lang ||
          language.language ||
          language.value ||
          ''
        ).toLowerCase();
        if (!lower) return null;
        const label =
          language.label ||
          lower.charAt(0).toUpperCase() + lower.slice(1);
        const rawModel = language.model;
        const model = hasStoredVoiceModel(rawModel)
          ? normalizeVoiceModelId(String(rawModel).trim())
          : null;
        return {
          value: lower,
          label,
          model,
        };
      }
      return null;
    })
    .filter(Boolean);

  if (legacyLangStrings && projectRow.language_models != null) {
    rows = rows.map((l) => {
      const fromMap = getLanguageModelForLang(
        projectRow.language_models,
        l.value
      );
      let model = null;
      if (hasStoredVoiceModel(fromMap)) {
        model = normalizeVoiceModelId(String(fromMap).trim());
      } else if (hasStoredVoiceModel(l.model)) {
        model = normalizeVoiceModelId(String(l.model).trim());
      }
      return { ...l, model };
    });
  }

  return rows;
}
